import type {
  CodeWert,
  FlexibleLeistung,
  HauptKomponente,
  LebenVersicherungsVertrag,
  LebenVertragKomponente,
  Tarif,
  TarifArt,
  ZusatzKomponente
} from 'ods-api'
import { ObjectUtils } from '@/utils/policy/ObjectUtils'
import { StringUtils } from '@/utils/policy/StringUtils'
import { ProductClassEnum } from '@/types/life/lifeTypes'

export class ContractUtils {
  public static getLebensvertragsKomponente(
    mainContract: HauptKomponente
  ): LebenVertragKomponente | null {
    if (mainContract.kapital) {
      return mainContract.kapital as LebenVertragKomponente
    } else if (mainContract.altersRente) {
      return mainContract.altersRente as LebenVertragKomponente
    } else if (mainContract.fonds) {
      return mainContract.fonds as LebenVertragKomponente
    } else if (mainContract.riester) {
      return mainContract.riester as LebenVertragKomponente
    } else if (mainContract.risiko) {
      return mainContract.risiko as LebenVertragKomponente
    } else if (mainContract.existenz) {
      return mainContract.existenz as LebenVertragKomponente
    } else {
      return null
    }
  }

  public static getLebensvertragsKomponenteFromSupplementaryComponent(
    supplementaryComponent: ZusatzKomponente
  ): LebenVertragKomponente | null {
    if (supplementaryComponent.risiko) {
      return supplementaryComponent.risiko as LebenVertragKomponente
    } else if (supplementaryComponent.existenz) {
      return supplementaryComponent.existenz as LebenVertragKomponente
    } else if (supplementaryComponent.hinterbliebenen) {
      return supplementaryComponent.hinterbliebenen as LebenVertragKomponente
    } else {
      return null
    }
  }

  public static getTarifArt(mainContract: HauptKomponente | undefined): TarifArt {
    const emptyTarifArt = { wert: '', code: '' }
    if (!mainContract) {
      return emptyTarifArt
    }
    const baseLifeContractData = this.getLebensvertragsKomponente(mainContract)
    return baseLifeContractData ? baseLifeContractData.tarif.art : emptyTarifArt
  }

  public static compareFlexibleLeistungReturnEarliest(
    prev: FlexibleLeistung,
    curr: FlexibleLeistung
  ): FlexibleLeistung {
    if (prev.kalenderJahr) {
      if (curr.kalenderJahr) {
        return prev.kalenderJahr < curr.kalenderJahr ? prev : curr
      }
      return prev
    }
    return curr
  }

  public static determineProductClass(mainContract: HauptKomponente): ProductClassEnum {
    const baseLifeContractData = this.getLebensvertragsKomponente(mainContract)
    if (!baseLifeContractData) {
      return ProductClassEnum.UNKNOWN
    }

    const tarifClass: CodeWert = baseLifeContractData.tarif.klasse as CodeWert
    const tarifGroup: CodeWert = baseLifeContractData.tarif.gruppe as CodeWert

    if (tarifClass?.code === 'FV') {
      return tarifGroup?.code === 'RUERUP'
        ? ProductClassEnum.RUE_RUP
        : ProductClassEnum.FONDS
    } else if (tarifClass?.code === 'RENTE') {
      return tarifGroup?.code === 'RUERUP'
        ? ProductClassEnum.RUE_RUP
        : ProductClassEnum.PENSION
    } else if (tarifClass?.code === 'KLV') {
      return ProductClassEnum.CAPITAL_LIFE
    } else if (tarifClass?.code === 'RLV') {
      return ProductClassEnum.RISK_LIFE
    } else if (tarifClass?.code === 'BU') {
      return ProductClassEnum.EXISTENCE
    } else if (tarifClass?.code === 'FRV-KTO') {
      return ProductClassEnum.RIESTER
    }
    return ProductClassEnum.UNKNOWN
  }

  public static getTarif(mainContract: HauptKomponente): Tarif | null {
    const baseLifeContractData = this.getLebensvertragsKomponente(mainContract)
    return baseLifeContractData ? baseLifeContractData.tarif : null
  }

  public static getPensionPaymentTriggerByTariff(tarif: Tarif | null): string | null {
    if (!tarif || ObjectUtils.isEmpty(tarif)) {
      return null
    }
    switch (tarif.art?.code) {
      case 'SBU':
        if (StringUtils.equalsIgnoreCase(tarif.gruppe?.code, 'AKTIV')) {
          return 'insuredBenefit.existence.paymentTrigger.seu'
        } else {
          return 'insuredBenefit.existence.paymentTrigger.bu'
        }
      case 'BU':
      case 'BUZ_R':
        return 'insuredBenefit.existence.paymentTrigger.bu'
      case 'EU':
      case 'SEU':
        return 'insuredBenefit.existence.paymentTrigger.seu'
      case 'GFV':
        return 'insuredBenefit.existence.paymentTrigger.gfv'
      default:
        return null
    }
  }

  public static hasContractGivenProductClass(
    lebenVersicherungsVertrag: LebenVersicherungsVertrag,
    givenProductClass: ProductClassEnum
  ): boolean {
    const productClass = ContractUtils.determineProductClass(
      lebenVersicherungsVertrag.hauptVersicherung
    )
    return productClass === givenProductClass
  }
}
